import React from 'react';
import SvgLoader, { SvgLoaderProps } from '../SvgLoader/SvgLoader';
import { useResponsiveDesign } from '../../../hooks/useResponsiveDesign';
import { useScrollFade } from '../../../hooks/useScrollFade';
import './SvgPositionedElement.css';

export interface Position {
  top?: number;    // pixels from top
  right?: number;  // pixels from right
  bottom?: number; // pixels from bottom
  left?: number;   // pixels from left
}

export interface SvgPositionedElementProps extends Omit<SvgLoaderProps, 'className'> {
  position?: Position;
  zIndex?: number;
  fixed?: boolean;
  pointerEvents?: 'auto' | 'none';
  className?: string;
  useThemeColors?: boolean;
  fadeTarget?: string; // CSS selector for fade trigger element
  'data-testid'?: string;
}

const SvgPositionedElement: React.FC<SvgPositionedElementProps> = ({
  position = {},
  zIndex = 0,
  fixed = false,
  pointerEvents = 'auto',
  className = '',
  useThemeColors = false,
  fadeTarget,
  'data-testid': dataTestId,
  ...svgLoaderProps
}) => {
  const { isDesktop } = useResponsiveDesign();
  const isMobileTablet = !isDesktop;

  // Use absolute positioning on mobile/tablet, fixed on desktop
  const effectiveFixed = fixed && !isMobileTablet;
  
  // Create CSS custom properties for positioning
  const cssCustomProperties: Record<string, string> = {
    '--svg-z-index': zIndex.toString(),
    '--svg-position': effectiveFixed ? 'fixed' : 'absolute',
    '--svg-pointer-events': pointerEvents,
  };

  // Add position coordinates if provided
  if (position.top !== undefined) {
    cssCustomProperties['--svg-top'] = `${position.top}px`;
  }
  if (position.right !== undefined) {
    cssCustomProperties['--svg-right'] = `${position.right}px`;
  }
  if (position.bottom !== undefined) {
    cssCustomProperties['--svg-bottom'] = `${position.bottom}px`;
  }
  if (position.left !== undefined) {
    cssCustomProperties['--svg-left'] = `${position.left}px`;
  }

  // Create data attributes for dynamic properties
  const dataAttributes = {
    'data-position': effectiveFixed ? 'fixed' : 'absolute',
    'data-pointer-events': pointerEvents,
    'data-dynamic-z-index': zIndex !== 0 ? 'true' : undefined,
    'data-dynamic-position': Object.keys(position).length > 0 ? 'true' : undefined,
    'data-viewport': isMobileTablet ? 'mobile-tablet' : 'desktop',
  };

  const isVisible = useScrollFade(fadeTarget, isDesktop);
  
  const themeClassName = useThemeColors ? 'svg-theme-aware' : '';
  const fadeClass = fadeTarget && !isVisible ? 'scroll-fade-out' : '';
  const combinedClassName = `svg-positioned-element ${themeClassName} ${fadeClass} ${className}`.trim();

  // Remove filter prop when using theme colors to avoid conflicts
  const filteredSvgLoaderProps = useThemeColors 
    ? { ...svgLoaderProps, filter: undefined }
    : svgLoaderProps;

  return (
    <div 
      className={combinedClassName} 
      style={cssCustomProperties}
      data-testid={dataTestId}
      {...dataAttributes}
    >
      <SvgLoader {...filteredSvgLoaderProps} />
    </div>
  );
};

export default SvgPositionedElement;
